import React, { useEffect, useId, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import api from "../../../services/api";
import { Dialog, errorText, fullName, useToast } from "./common";

const EMPTY = {
  name: "",
  surname: "",
  username: "",
  email: "",
  role: "student",
  password: "",
  grade: "",
  school: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CreateUserModal = ({ open, onClose, onCreated }) => {
  const { notify } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);
  const formId = useId();

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setError(null);
      setTouched(false);
      setShowPw(false);
    }
  }, [open]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const problems = {
    name: !form.name.trim() && "First name is required.",
    surname: !form.surname.trim() && "Surname is required.",
    username: !form.username.trim() && "Username is required.",
    email: !EMAIL_RE.test(form.email.trim()) && "Enter a valid email address.",
    password: form.password.length < 8 && "Password must be at least 8 characters.",
  };
  const valid = !Object.values(problems).some(Boolean);
  const show = (key) => touched && problems[key];

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || saving) return;
    setSaving(true);
    setError(null);
    const body = {
      name: form.name.trim(),
      surname: form.surname.trim(),
      username: form.username.trim(),
      email: form.email.trim(),
      role: form.role,
      password: form.password,
    };
    if (form.grade.trim()) body.grade = form.grade.trim();
    if (form.school.trim()) body.school = form.school.trim();
    try {
      const res = await api.post("/admin/users", body);
      notify(`Account created for ${fullName(res.data || body)}.`);
      onCreated?.(res.data);
      onClose();
    } catch (err) {
      setError(errorText(err, "Couldn't create that account."));
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, props = {}) => (
    <div className="eg-field">
      <label className="eg-label" htmlFor={`${formId}-${key}`}>
        {label}
      </label>
      <input
        id={`${formId}-${key}`}
        className={`eg-input ${show(key) ? "admin-input-invalid" : ""}`}
        value={form[key]}
        onChange={set(key)}
        aria-invalid={show(key) ? true : undefined}
        aria-describedby={show(key) ? `${formId}-${key}-err` : undefined}
        {...props}
      />
      {show(key) && (
        <small id={`${formId}-${key}-err`} className="admin-field-error">
          {problems[key]}
        </small>
      )}
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={saving ? () => {} : onClose}
      title="Add a user"
      subtitle="The account is created already verified, so they can sign in straight away."
      footer={
        <>
          <button type="button" className="eg-btn eg-btn-outline" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="eg-btn eg-btn-primary" disabled={saving}>
            {saving ? "Creating…" : "Create account"}
          </button>
        </>
      }
    >
      <form id={formId} className="admin-form" onSubmit={submit} noValidate>
        <div className="admin-form-grid">
          {field("name", "First name", { autoComplete: "off" })}
          {field("surname", "Surname", { autoComplete: "off" })}
          {field("username", "Username", { autoComplete: "off" })}
          {field("email", "Email", { type: "email", autoComplete: "off" })}
          <div className="eg-field">
            <label className="eg-label" htmlFor={`${formId}-role`}>
              Role
            </label>
            <select id={`${formId}-role`} className="eg-input" value={form.role} onChange={set("role")}>
              <option value="student">Student</option>
              <option value="teacher">Teacher</option>
            </select>
          </div>
          <div className="eg-field">
            <label className="eg-label" htmlFor={`${formId}-password`}>
              Temporary password
            </label>
            <div className="admin-password">
              <input
                id={`${formId}-password`}
                className={`eg-input ${show("password") ? "admin-input-invalid" : ""}`}
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={set("password")}
                autoComplete="new-password"
                aria-describedby={`${formId}-pw-hint`}
                aria-invalid={show("password") ? true : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <small id={`${formId}-pw-hint`} className={show("password") ? "admin-field-error" : "admin-field-hint"}>
              At least 8 characters. Ask them to change it after signing in.
            </small>
          </div>
          {field("grade", form.role === "student" ? "Grade (optional)" : "Grade taught (optional)", {
            placeholder: "e.g. 10",
          })}
          {field("school", "School (optional)")}
        </div>
        {error && <div className="admin-error admin-error-tight">{error}</div>}
      </form>
    </Dialog>
  );
};

export default CreateUserModal;
